import type { FC } from 'react';
import { Button } from '../../components/Button';
import { AUTH_TYPES, MODE_LABELS } from './constants';
import styles from './ProxyConfig.module.css';

interface ProfileCardProps {
  profile: any;
  testResult: any;
  onEdit: (profile: any) => void;
  onTest: (profile: any) => void;
  onDelete: (id: string, name: string) => void;
}

export const ProfileCard: FC<ProfileCardProps> = ({ profile, testResult, onEdit, onTest, onDelete }) => {
  const mode = profile.portal || '';

  const mleConfigured =
    profile.mleConfig?.keyId && profile.mleConfig?.serverCertificate && profile.mleConfig?.privateKey;

  const cybersourceHttpSigConfigured =
    profile.authType === 'http_signature' &&
    profile.authConfig?.merchantId &&
    profile.authConfig?.merchantKeyId &&
    profile.authConfig?.merchantSecretKey;

  const cybersourceJwtConfigured =
    profile.authType === 'jwt' &&
    profile.authConfig?.merchantId &&
    profile.authConfig?.keyAlias &&
    profile.authConfig?.keyPassword &&
    profile.authConfig?.keyFileContent;

  const authConfigured =
    (mode === 'authorize' && profile.authConfig?.apiLoginId && profile.authConfig?.transactionKey) ||
    (mode === 'cybersource' && (cybersourceHttpSigConfigured || cybersourceJwtConfigured)) ||
    (profile.authType === 'mtls' &&
      profile.authConfig?.userId &&
      profile.authConfig?.cert &&
      profile.authConfig?.key) ||
    (profile.authType === 'xpay' && profile.authConfig?.api_key && profile.authConfig?.shared_secret);

  return (
    <div className={styles.profileCard}>
      <div className={styles.profileHeader}>
        <div className={styles.profileTitle}>
          <h3 className={styles.profileName}>{profile.name}</h3>
          <p className={styles.profileDescription}>{profile.description || 'No description provided'}</p>
        </div>
      </div>

      <div className={styles.badgeGroup}>
        <span className={styles.badge} data-type="info">
          Mode: {MODE_LABELS[mode] || 'Not Configured'}
        </span>

        {mode === 'vdp' && (
          <>
            <span className={styles.badge} data-type={authConfigured ? 'mle-enabled' : 'mle-disabled'}>
              Auth: {AUTH_TYPES.find((t) => t.value === profile.authType)?.label || profile.authType}
            </span>
            <span className={styles.badge} data-type={mleConfigured ? 'mle-enabled' : 'mle-disabled'}>
              MLE
            </span>
          </>
        )}

        {mode === 'authorize' && (
          <span className={styles.badge} data-type={authConfigured ? 'mle-enabled' : 'mle-disabled'}>
            Auth: Authorize.Net
          </span>
        )}

        {mode === 'cybersource' && (
          <span className={styles.badge} data-type={authConfigured ? 'mle-enabled' : 'mle-disabled'}>
            Auth:{' '}
            {profile.authType === 'http_signature'
              ? 'HTTP Signature'
              : profile.authType === 'jwt'
                ? 'JWT'
                : 'Not configured'}
          </span>
        )}
      </div>

      {testResult && !testResult.loading && (
        <div className={styles.testResult} data-success={testResult.success ? 'true' : 'false'}>
          <strong>{testResult.success ? '✓ Test Successful' : '✗ Test Failed'}</strong>
          {testResult.summary && testResult.summary.length > 0 ? (
            <div className={styles.testSteps}>
              {testResult.summary.map((step: string, index: number) => (
                <div key={index} className={styles.testStep}>
                  {step}
                </div>
              ))}
            </div>
          ) : (
            <p>{testResult.message}</p>
          )}
          {testResult.error && <p>Error: {testResult.error}</p>}
        </div>
      )}

      <div className={styles.profileActions}>
        <Button variant="secondary" className={styles.smallButton} onClick={() => onEdit(profile)}>
          Edit
        </Button>
        <Button
          variant="success"
          className={styles.smallButton}
          onClick={() => onTest(profile)}
          disabled={testResult?.loading}
        >
          {testResult?.loading ? 'Testing...' : 'Test'}
        </Button>
        <Button variant="danger" className={styles.smallButton} onClick={() => onDelete(profile.id, profile.name)}>
          Delete
        </Button>
      </div>
    </div>
  );
};
